/**
 * Administration → Settings. Every string on the screen.
 *
 * English throughout, as the rest of this screen is. Where the phone has a
 * Somali word for the same thing it is used; where it has none, English — and
 * "Cancel" rather than "Jooji".
 */
export const COPY = {
  title: 'Settings',
  intro: 'Your restaurant’s details, and how the till behaves.',

  unsaved: 'Unsaved changes',
  save: 'Save changes',
  saving: 'Saving…',
  discard: 'Discard',
  saved: 'Settings saved',

  /* ── the restaurant ── */
  restaurantTitle: 'Restaurant',
  restaurantBody: 'The name, picture and details that appear on receipts and around the app.',
  nameLabel: 'Restaurant name',
  namePlaceholder: 'Xamar Restaurant',
  numberLabel: 'Phone number',
  numberPlaceholder: '252 61 575 3832',
  numberHint: 'Printed on every receipt.',
  locationLabel: 'Location',
  locationPlaceholder: 'Hodan, Mogadishu',
  categoryLabel: 'Category',
  categoryPlaceholder: 'Restaurant',
  markLabel: 'Currency mark',
  markPlaceholder: '$',
  markPreview: (mark) => `Prices will read ${mark}182.00`,
  markLowercase: (typed, stored) =>
    `The server stores this in lower case — ${typed} is saved as ${stored}.`,

  /* ── the picture ── */
  logoTitle: 'Picture',
  logoBody: 'Shown beside your name in the app, and on printed work that carries a letterhead.',
  logoEmpty: 'No picture yet',
  logoChoose: 'Upload a picture',
  logoReplace: 'Replace',
  logoRemove: 'Remove',
  logoUploading: 'Uploading…',
  logoUploaded: 'Picture uploaded — press Save to keep it',
  logoRemoved: 'Picture removed — press Save to keep it',
  logoTooBig: 'That image is too large. Pick one under 15MB.',
  logoWidth: 'Width',
  logoHeight: 'Height',
  logoSizeHint: (w, h) => `Drawn at ${w} × ${h} pixels.`,
  logoSizeNote: 'Between 24 and 600 pixels.',
  logoLock: 'Keep it square',
  logoAccepted: 'JPG, PNG or WebP.',

  /* ── payment accounts ── */
  accountsTitle: 'Payment accounts',
  accountsBody:
    'Where customers send money. Each one prints on the receipt as “EVC: 252615753832”, and is a button the cashier picks from when marking an order paid.',
  accountsEmpty: 'No accounts yet. Only Cash will be offered at the till.',
  accountLabel: 'Name',
  accountLabelPlaceholder: 'EVC',
  accountNumber: 'Number',
  accountNumberPlaceholder: '252615753832',
  accountAdd: 'Add account',
  accountsFull: 'That is as many as a receipt can carry.',
  cashNote: 'Cash is always offered and is not listed here.',

  /* ── VAT ── */
  vatTitle: 'VAT',
  vatBody:
    'Output VAT on customer orders. Nothing to do with purchases — stock never carries VAT.',
  vatLabel: 'VAT percentage',
  vatNone: 'Leave it at 0 if you do not charge VAT.',
  autoVatLabel: 'Add VAT to every order',
  autoVatBody:
    'A new order carries VAT without anyone asking for it. A cashier can still leave VAT off a printed receipt, but the order keeps it so the figures stay right for filing.',

  /* ── the receipt printer ── */
  printerTitle: 'Receipt printer',
  printerBody:
    'A network printer for customer receipts, as “address:port”. Leave it empty and receipts print on this device’s own printer instead.',
  printerLabel: 'Address',
  printerPlaceholder: '192.168.18.70:9100',
  printerClear: 'Use this device’s printer',

  /* ── stock units ── */
  unitsTitle: 'Stock units',
  unitsBody:
    'What a stock item can be measured in. Removing one never breaks the items already using it — an item keeps the unit it was saved with.',
  unitsEmpty: 'No units yet.',
  unitAdd: 'Add unit',
  unitPlaceholder: 'kg',
  unitsFull: 'That is as many units as one list should hold.',

  /* ── SMS ── */
  smsTitle: 'SMS',
  smsBody: 'What a customer is sent when you remind them about money owed.',
  smsLabel: 'Default message',
  smsPlaceholder: 'Fadlan iska bixi lacagta laguugu leeyahay oo ah',
  smsHint: 'The amount is added to the end of this.',
  smsCount: (used, max) => `${used} of ${max}`,
  smsCustomTitle: 'Saved messages',
  smsCustomBody: 'Other wordings you can pick instead of the default.',
  smsCustomEmpty: 'No saved messages.',
  smsCustomName: 'Name',
  smsCustomNamePlaceholder: 'Polite reminder',
  smsCustomMessage: 'Message',
  smsCustomAdd: 'Add message',
  smsCustomFull: 'That is as many saved messages as one list should hold.',

  /* ── the two switches, deliberately without a heading ── */
  autoPrintLabel: 'Print the receipt automatically',
  autoPrintBody:
    'Placing an order also prints the customer receipt, alongside the kitchen tickets. Leave it off if receipts are only printed on request.',

  publicMenuLabel: 'Public menu',
  publicMenuBody:
    'Lets anyone who scans your QR code read the menu on their phone. The code itself is under the Public menu tab.',
  publicMenuClosing:
    'Scanning will stop working for customers as soon as you save. Codes you have printed are not wasted — the address never changes, so switching it back on makes every one of them work again.',

  /* ── shared row controls ── */
  remove: 'Remove',

  /* ── what the server says ── */
  refusedTitle: 'The server would not allow it',
  refused:
    'Only a manager can change the restaurant’s details, and this account is not one. Nothing was saved.',
  invalidTitle: 'The server would not take one of these values',
  failed: 'Could not save. Check the connection and try again.',
  fixFirst: 'Fix the highlighted fields first.',
};
